// 학부모 사이드바 컴포넌트

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PaymentIcon from '@mui/icons-material/Payment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChatIcon from '@mui/icons-material/Chat';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useSupabaseParentAuth } from '../../hooks/useSupabaseParentAuth';
import { getAccessToken } from '../../../../lib/authUtils';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  scheduleType?: 'weekly' | 'period'; // 특정 스케줄 유형에서만 표시
}

const allMenuItems: MenuItem[] = [
  { id: 'dashboard', label: '홈', icon: <HomeIcon />, path: '/parent/dashboard' },
  { id: 'children', label: '자녀 관리', icon: <PeopleIcon />, path: '/parent/children' },
  { id: 'attendance', label: '출석 현황', icon: <CheckCircleIcon />, path: '/parent/attendance' },
  { id: 'study-time', label: '순공부시간', icon: <AccessTimeIcon />, path: '/parent/study-time' },
  { id: 'schedule', label: '주간 스케줄', icon: <CalendarMonthIcon />, path: '/parent/schedule', scheduleType: 'weekly' },
  { id: 'period-schedule', label: '교시 스케줄', icon: <CalendarMonthIcon />, path: '/parent/period-schedule', scheduleType: 'period' },
  { id: 'payment', label: '이용권 내역', icon: <PaymentIcon />, path: '/parent/payment' },
  { id: 'penalty', label: '벌점 현황', icon: <WarningAmberIcon />, path: '/parent/penalty' },
  { id: 'notice', label: '알림장', icon: <NotificationsIcon />, path: '/parent/notice' },
  { id: 'counseling', label: '상담 기록', icon: <ChatIcon />, path: '/parent/counseling' },
];

export default function ParentSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { parent, signOut } = useSupabaseParentAuth();
  const [scheduleType, setScheduleType] = useState<string>('period');

  useEffect(() => {
    loadScheduleType();
  }, []);

  const loadScheduleType = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      // 자녀 목록에서 organization_id 가져오기
      const childRes = await fetch('/api/v1/parent/children', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!childRes.ok) return;

      const children = await childRes.json();
      if (children.length === 0) return;

      const orgId = children[0].organization_id;
      if (!orgId) return;

      // 조직의 schedule_type 조회
      const orgRes = await fetch(`/api/v1/owner/organization/${orgId}/schedule-type`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!orgRes.ok) return;

      const orgData = await orgRes.json();
      setScheduleType(orgData.schedule_type || 'weekly');
    } catch (err) {
      console.error('스케줄 유형 로드 실패:', err);
    }
  };

  // schedule_type에 따라 메뉴 필터링
  const menuItems = allMenuItems.filter((item) => {
    if (!item.scheduleType) return true;
    return item.scheduleType === scheduleType;
  });

  const handleMenuClick = (path: string) => {
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('로그아웃 에러:', error);
    } finally {
      navigate('/parent/login', { replace: true });
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 사용자 정보 */}
      <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {parent?.name || '학부모'}님
        </Typography>
        <Typography variant="caption">{parent?.phone}</Typography>
      </Box>

      <Divider />

      {/* 메뉴 목록 */}
      <List sx={{ flexGrow: 1, pt: 2 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);

          return (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleMenuClick(item.path)}
                selected={isActive}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? 'primary.main' : 'text.secondary',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* 하단 메뉴 */}
      <List sx={{ pb: 2 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/parent/settings')} sx={{ mx: 1, borderRadius: 1 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="설정" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ mx: 1, borderRadius: 1 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="로그아웃" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
}
